import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex, keepLocalMediaPreview } from './helpers';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';
import {
  conversationCacheKey,
  conversationSessionCache,
  rememberConversation,
} from 'dashboard/helper/conversationSessionCache';

const state = {
  allConversations: [],
  attachments: {},
  listLoadingStatus: true,
  // Aceleres: distingue "lista vazia de verdade" de "falhou ao carregar" — sem isso
  // um 500/timeout caia no empty state "nenhuma conversa" e o usuario so tinha F5.
  listErrorStatus: false,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: 'last_activity_at_desc',
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
};

const getConversationById = _state => conversationId => {
  const normalizedConversationId = Number(conversationId);
  return _state.allConversations.find(
    c => Number(c.id) === normalizedConversationId
  );
};

const getMessageSortKey = message => {
  const createdAt = Number(message?.created_at);
  if (Number.isFinite(createdAt)) {
    return createdAt;
  }

  const messageId = Number(message?.id);
  if (Number.isFinite(messageId)) {
    return messageId;
  }

  return 0;
};

const sortConversationMessages = messages => {
  return [...messages].sort((left, right) => {
    const leftKey = getMessageSortKey(left);
    const rightKey = getMessageSortKey(right);

    if (leftKey === rightKey) {
      return Number(left?.id || 0) - Number(right?.id || 0);
    }

    return leftKey > rightKey ? 1 : -1;
  });
};

const mergeConversationMessages = (
  existingMessages = [],
  incomingMessages = []
) => {
  const mergedMessages = [];
  const seenMessageIds = new Set();

  [...existingMessages, ...incomingMessages].forEach(message => {
    const messageId = message?.id;

    if (messageId && seenMessageIds.has(messageId)) {
      return;
    }

    if (messageId) {
      seenMessageIds.add(messageId);
    }

    mergedMessages.push(message);
  });

  return sortConversationMessages(mergedMessages);
};

const messagesForCurrentCycle = (conversation, messages = []) => {
  const cutoff = conversation?.meta?.history?.current_cycle_started_at;
  if (!cutoff) return messages;

  const cutoffTime = new Date(cutoff).getTime();
  if (!Number.isFinite(cutoffTime)) return messages;

  return messages.filter(message => {
    const messageTime = new Date(message?.created_at).getTime();
    return !Number.isFinite(messageTime) || messageTime > cutoffTime;
  });
};

// Aceleres — uma mudança de status já confirmada pelo servidor não pode ser
// desfeita por um payload que já estava em voo quando ela aconteceu.
//
// Medido em 24/08/2026: concluir enquanto a primeira página de Meus ainda
// estava sendo buscada deixava a conversa na caixa. O `PUT toggle_status`
// respondia `resolved`, `CHANGE_CONVERSATION_STATUS` gravava — e logo depois a
// resposta da lista (pedida ANTES da conclusão, portanto ainda com
// `status: open`) substituía o registro pelo antigo. Como as caixas
// operacionais escondem exatamente o `resolved`, o cartão voltava a aparecer e
// só saía no refresh seguinte ou num F5. A mesma escrita se perdia por inteiro
// quando a conversa nem estava no store no instante do commit: a mutation cai
// num `|| {}` e não avisa ninguém.
//
// Encerrar o registro no primeiro payload que CONCORDA não serve: medido em
// 24/08/2026, o broadcast `conversation.updated` (já com `resolved`) chegava
// 1,3 s antes da resposta de lista pedida ANTES da conclusão — apagar ali
// deixava o payload atrasado passar e a conversa voltava para Meus. O que o
// payload concordante dá é uma MARCA D'ÁGUA: o `updated_at` a partir do qual o
// servidor já conhece a mudança. Daí em diante, quem discorda só vence se for
// comprovadamente posterior a ela — isso é uma reabertura de verdade, não um
// atraso. A idade (30 s) fecha o caso quando nenhum payload concordante vem.
const ACELERES_PENDING_STATUS_TTL = 30000;
const pendingStatusWrites = new Map();

const rememberPendingStatus = (conversationId, status, snoozedUntil) => {
  const id = Number(conversationId);
  if (!id || !status) return;
  pendingStatusWrites.set(id, {
    status,
    snoozedUntil,
    at: Date.now(),
    watermark: 0,
  });
};

const pendingStatusFor = conversation => {
  const id = Number(conversation?.id);
  const pending = id ? pendingStatusWrites.get(id) : null;
  if (!pending) return null;
  // Payload que nem descreve status nao tem o que ser corrigido -- e nao pode
  // ganhar um `status` que ele nao trazia.
  if (!conversation?.status) return null;

  if (Date.now() - pending.at > ACELERES_PENDING_STATUS_TTL) {
    pendingStatusWrites.delete(id);
    return null;
  }

  const updatedAt = Number(conversation.updated_at) || 0;

  if (conversation.status === pending.status) {
    // O servidor já conhece a mudança. Guarda a marca d'água e mantém o
    // registro: uma resposta pedida ANTES desta ainda pode chegar depois dela.
    if (updatedAt > pending.watermark) pending.watermark = updatedAt;
    return null;
  }

  if (pending.watermark && updatedAt >= pending.watermark) {
    pendingStatusWrites.delete(id);
    return null;
  }

  return {
    status: pending.status,
    snoozed_until: pending.snoozedUntil ?? null,
  };
};

// Aceleres — o "Iniciar atendimento" sofre exatamente a corrida descrita acima
// para o status, e pelo mesmo motivo: o clique grava assignee + a marca de
// atendimento iniciado, mas uma listagem PEDIDA ANTES do clique chega depois
// dele e devolve a conversa ao estado anterior (sem responsável, sem
// `attendance_started`). O gate do compositor volta a desenhar o botão e o
// atendimento parece não ter começado — o vendedor clica de novo. Medido em
// homologação em 09/09/2026, conversa 76 da conta 2569: o clique gravou no
// banco (`attendance_started: true`, assignee 6320) e 10 s depois o registro no
// store estava de volta em `additional_attributes: {}` e sem responsável.
//
// A regra é a mesma do status: um payload que CONCORDA não encerra o registro,
// só deixa a marca d'água; quem discorda só vence se for comprovadamente
// posterior a ela. A idade fecha o caso quando nenhum payload concordante vem.
const ACELERES_PENDING_START_TTL = 30000;
const pendingStartWrites = new Map();

const rememberPendingStart = (conversationId, assignee, attributes) => {
  const id = Number(conversationId);
  if (!id) return;
  pendingStartWrites.set(id, {
    assignee: assignee || null,
    attributes: attributes || {},
    at: Date.now(),
    watermark: 0,
  });
};

const pendingStartFor = conversation => {
  const id = Number(conversation?.id);
  const pending = id ? pendingStartWrites.get(id) : null;
  if (!pending) return null;
  // Payload que nem descreve os atributos não tem o que ser corrigido — e não
  // pode ganhar um `additional_attributes` que ele não trazia.
  if (!conversation?.additional_attributes) return null;

  if (Date.now() - pending.at > ACELERES_PENDING_START_TTL) {
    pendingStartWrites.delete(id);
    return null;
  }

  const updatedAt = Number(conversation.updated_at) || 0;
  // Nem todo payload descreve o responsável (o broadcast de mensagem, por
  // exemplo, traz `additional_attributes` sem `meta`). Cobrar responsável de
  // quem não fala dele transformava o payload em discordante, e ele apagava o
  // registro — foi o que deixou a listagem antiga passar mesmo com o escudo
  // ligado, medido em homologação em 09/09/2026.
  const describesAssignee =
    conversation.meta !== undefined &&
    Object.hasOwn(conversation.meta || {}, 'assignee');
  const assigneeId = conversation.meta?.assignee?.id;
  const agrees =
    conversation.additional_attributes.attendance_started === true &&
    (!pending.assignee?.id ||
      !describesAssignee ||
      Number(assigneeId) === Number(pending.assignee.id));

  if (agrees) {
    if (updatedAt > pending.watermark) pending.watermark = updatedAt;
    return null;
  }

  if (pending.watermark && updatedAt >= pending.watermark) {
    pendingStartWrites.delete(id);
    return null;
  }

  return {
    additional_attributes: {
      ...conversation.additional_attributes,
      ...pending.attributes,
    },
    ...(pending.assignee
      ? {
          meta: {
            ...(conversation.meta || {}),
            assignee: pending.assignee,
          },
        }
      : {}),
  };
};

const preserveConversationLoadState = (
  rawIncomingConversation,
  currentConversation = null,
  force = false
) => {
  // Compatibilidade com respostas antigas do serializer: durante a
  // transicao, os campos de pin podem chegar dentro de meta.
  const incomingConversation = {
    ...rawIncomingConversation,
    is_pinned:
      rawIncomingConversation?.is_pinned ??
      rawIncomingConversation?.meta?.is_pinned ??
      false,
    pinned_at:
      rawIncomingConversation?.pinned_at ??
      rawIncomingConversation?.meta?.pinned_at ??
      null,
  };
  const cachedConversation =
    currentConversation ||
    conversationSessionCache.get(conversationCacheKey(incomingConversation));
  const hasLoadedState =
    force ||
    cachedConversation?.dataFetched === true ||
    cachedConversation?.prefetchProgress !== undefined;
  const incomingHasLoadedState =
    incomingConversation?.dataFetched === true ||
    incomingConversation?.prefetchProgress !== undefined;
  const preservedPin =
    cachedConversation?.is_pinned && incomingConversation?.is_pinned === false
      ? {
          ...incomingConversation,
          is_pinned: true,
          pinned_at: cachedConversation.pinned_at,
        }
      : incomingConversation;

  // Vale para os tres caminhos: e o payload que chega desatualizado, nao o
  // estado carregado, que precisa ser corrigido.
  const statusLocal = pendingStatusFor(incomingConversation) || {};
  const startLocal = pendingStartFor(incomingConversation) || {};
  const authoritativeMessageWindow =
    incomingConversation?.meta?.history &&
    Array.isArray(incomingConversation.messages);

  if (!cachedConversation || (!hasLoadedState && !incomingHasLoadedState)) {
    return rememberConversation({
      ...incomingConversation,
      ...statusLocal,
      ...startLocal,
    });
  }

  if (incomingHasLoadedState) {
    const currentCycleConversation = {
      ...cachedConversation,
      ...incomingConversation,
      meta: {
        ...(cachedConversation?.meta || {}),
        ...(incomingConversation?.meta || {}),
      },
    };
    // A resposta de `ConversationApi.show` traz `meta.history` e uma janela
    // autoritativa já filtrada pelo trecho do atendente. Não mesclar essa
    // janela com mensagens antigas da sessão: após uma transferência, o
    // cache podia continuar exibindo os trechos dos atendentes anteriores,
    // embora o endpoint já devolvesse somente o trecho atual.
    return rememberConversation({
      ...preservedPin,
      ...statusLocal,
      ...startLocal,
      allMessagesLoaded:
        incomingConversation.allMessagesLoaded ??
        cachedConversation.allMessagesLoaded,
      messages: authoritativeMessageWindow
        ? incomingConversation.messages
        : mergeConversationMessages(
            messagesForCurrentCycle(
              currentCycleConversation,
              cachedConversation.messages || []
            ),
            messagesForCurrentCycle(
              currentCycleConversation,
              incomingConversation.messages || []
            )
          ),
      dataFetched:
        incomingConversation.dataFetched ?? cachedConversation.dataFetched,
      conversationDetailsFetched:
        incomingConversation.conversationDetailsFetched ??
        cachedConversation.conversationDetailsFetched,
      showPreviousMessages:
        cachedConversation.showPreviousMessages === true ||
        incomingConversation.showPreviousMessages === true,
      prefetchProgress: Math.max(
        Number(cachedConversation.prefetchProgress || 0),
        Number(incomingConversation.prefetchProgress || 0)
      ),
    });
  }

  return rememberConversation({
    ...preservedPin,
    ...statusLocal,
    ...startLocal,
    allMessagesLoaded: cachedConversation.allMessagesLoaded,
    messages: authoritativeMessageWindow
      ? incomingConversation.messages
      : messagesForCurrentCycle(
          { ...cachedConversation, ...incomingConversation },
          cachedConversation.messages
        ),
    dataFetched: cachedConversation.dataFetched,
    conversationDetailsFetched: cachedConversation.conversationDetailsFetched,
    showPreviousMessages: cachedConversation.showPreviousMessages === true,
    prefetchProgress: cachedConversation.prefetchProgress,
  });
};

// mutations
export const mutations = {
  [types.RESTORE_CONVERSATION_FROM_SESSION_CACHE](_state, conversationId) {
    const normalizedConversationId = Number(conversationId);
    const cachedConversation = [...conversationSessionCache.values()].find(
      conversation => Number(conversation?.id) === normalizedConversationId
    );

    if (!cachedConversation) {
      return;
    }

    const indexInCurrentList = _state.allConversations.findIndex(
      conversation => Number(conversation.id) === normalizedConversationId
    );

    if (indexInCurrentList < 0) {
      _state.allConversations.push(cachedConversation);
      return;
    }

    _state.allConversations[indexInCurrentList] = preserveConversationLoadState(
      cachedConversation,
      _state.allConversations[indexInCurrentList]
    );
  },
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => Number(c.id) === Number(conversation.id)
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(preserveConversationLoadState(conversation));
      } else if (Number(conversation.id) !== Number(_state.selectedChatId)) {
        // If the conversation is already in the list, replace it
        // Added this to fix the issue of the conversation not being updated
        // When reconnecting to the websocket. If the selectedChatId is not the same as
        // the conversation.id in the store, replace the existing conversation with the new one.
        // If the conversation was prefetched already, preserve the loaded
        // message window so the preload survives list refreshes / websocket syncs.
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = preserveConversationLoadState(
          conversation,
          existingConversation
        );
      } else {
        // If the conversation is already in the list and selectedChatId is the same,
        // replace all data except the messages array, attachments, dataFetched, allMessagesLoaded
        const existingConversation = newAllConversations[indexInCurrentList];
        newAllConversations[indexInCurrentList] = preserveConversationLoadState(
          conversation,
          existingConversation,
          true
        );
      }
    });
    _state.allConversations = newAllConversations;
  },
  [types.REPLACE_ALL_CONVERSATION](_state, conversationList) {
    const currentConversations = new Map(
      _state.allConversations.map(conversation => [
        Number(conversation.id),
        conversation,
      ])
    );
    const nextConversations = conversationList.map(conversation =>
      preserveConversationLoadState(
        conversation,
        currentConversations.get(Number(conversation.id))
      )
    );

    // A conversa aberta pode estar fora da página/caixa atual. Mantê-la no
    // store evita desmontar o histórico enquanto o usuário troca de aba; a
    // lista visual continua filtrando esse item pela visão ativa.
    const selectedConversation = _state.allConversations.find(
      conversation => Number(conversation.id) === Number(_state.selectedChatId)
    );
    if (
      selectedConversation &&
      !nextConversations.some(
        conversation =>
          Number(conversation.id) === Number(selectedConversation.id)
      )
    ) {
      nextConversations.push(selectedConversation);
    }

    _state.allConversations = nextConversations;
  },
  [types.EMPTY_ALL_CONVERSATION](
    _state,
    { preserveSelectedConversation = false } = {}
  ) {
    const selectedConversation = preserveSelectedConversation
      ? _state.allConversations.find(
          conversation => conversation.id === _state.selectedChatId
        )
      : null;

    _state.allConversations = selectedConversation
      ? [selectedConversation]
      : [];
    if (!selectedConversation) {
      _state.selectedChatId = null;
    }
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (!data.length) return;

    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;

    chat.showPreviousMessages = true;

    const existingMessageIds = new Set((chat.messages || []).map(m => m.id));
    const messagesToAdd = data.filter(
      message => !existingMessageIds.has(message.id)
    );
    if (!messagesToAdd.length) return;

    chat.messages.unshift(...messagesToAdd);
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  // Aceleres: o nome diz "missing" e a intencao sempre foi COMPLETAR o que
  // falta -- mas a linha final substituia o array inteiro, e era ela que jogava
  // o atendente para a ultima mensagem no meio da leitura.
  //
  // Quem mais chama isto e a acao `getConversation`, com o payload do endpoint
  // `conversations#show` -- que traz apenas as ULTIMAS 20 mensagens (ver o
  // `limit(20)` em _conversation.json.jbuilder). Entao qualquer releitura da
  // conversa aberta (reconciliacao de preview apos mensagem em tempo real,
  // prefetch de detalhes, atribuicao) apagava todo o historico que o atendente
  // tinha trazido rolando para cima e deixava so 20 mensagens. A lista encolhia
  // de milhares de pixels para algumas centenas, o navegador prendia o scrollTop
  // no novo maximo -- e isso e literalmente "ir para a ultima mensagem".
  // Quanto mais historico carregado, maior o tombo: por isso aparecia junto com
  // o carregamento por partes.
  //
  // Mesclar resolve sem perder nada: mergeConversationMessages deduplica por id
  // e reordena, entao a janela de 20 do `show` entra sem derrubar o resto. A
  // outra chamada (syncActiveConversationMessages) ja mandava a lista completa
  // e continua igual, porque mesclar a lista inteira com ela mesma nao muda nada.
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;

    chat.messages = mergeConversationMessages(chat.messages || [], data);
  },

  [types.SET_CHAT_DATA_FETCHED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.dataFetched = true;
      // The list preloader already has the conversation card data and the
      // initial message window. Avoid issuing a second show request when the
      // user opens that conversation for the first time.
      chat.conversationDetailsFetched = true;
    }
  },

  [types.SET_CONVERSATION_PREFETCH_PROGRESS](
    _state,
    { conversationId, progress }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      const normalizedProgress = Number(progress) || 0;
      chat.prefetchProgress = Math.max(0, Math.min(normalizedProgress, 100));
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, { conversationId, assignee }) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.meta.assignee = assignee;
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.meta.team = team;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId, unreadCount }
  ) {
    const [chat] = _state.allConversations.filter(
      c => Number(c.id) === Number(conversationId)
    );
    if (chat) {
      chat.last_activity_at = lastActivityAt;
      chat.timestamp = lastActivityAt;
      if (unreadCount !== undefined && unreadCount !== null) {
        chat.unread_count = Number(unreadCount) || 0;
      }
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](
    _state,
    { conversationId, customAttributes }
  ) {
    const conversation = _state.allConversations.find(
      c => c.id === conversationId
    );
    if (conversation) {
      conversation.custom_attributes = {
        ...conversation.custom_attributes,
        ...customAttributes,
      };
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    // Registrado ANTES de escrever: a conversa pode nem estar no store agora
    // (lista em transicao, caixa trocada, item despejado) e nesse caso a linha
    // seguinte escreveria num objeto descartavel, em silencio.
    rememberPendingStatus(conversationId, status, snoozedUntil);
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  // Registrado ANTES da escrita otimista, pelo mesmo motivo do status: a
  // conversa pode nem estar no store neste instante (lista em transição, caixa
  // trocada) e a escrita cairia num objeto descartável, em silêncio.
  [types.ACELERES_MARK_ATTENDANCE_STARTED](
    _state,
    { conversationId, assignee, attributes }
  ) {
    rememberPendingStart(conversationId, assignee, attributes);
  },

  [types.MUTE_CONVERSATION](
    _state,
    { conversationId, contactId, additionalAttributes }
  ) {
    _state.allConversations.forEach(chat => {
      if (chat.id === conversationId || chat.meta?.sender?.id === contactId) {
        chat.muted = true;
        if (chat.meta?.sender) chat.meta.sender.blocked = true;
        if (chat.meta?.sender && additionalAttributes) {
          chat.meta.sender.additional_attributes = additionalAttributes;
        }
      }
    });
  },

  [types.UNMUTE_CONVERSATION](
    _state,
    { conversationId, contactId, additionalAttributes }
  ) {
    _state.allConversations.forEach(chat => {
      if (chat.id === conversationId || chat.meta?.sender?.id === contactId) {
        chat.muted = false;
        if (chat.meta?.sender) chat.meta.sender.blocked = false;
        if (chat.meta?.sender && additionalAttributes) {
          chat.meta.sender.additional_attributes = additionalAttributes;
        }
      }
    });
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE]({ allConversations, selectedChatId }, message) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      chat.messages.splice(
        pendingMessageIndex,
        1,
        keepLocalMediaPreview(chat.messages[pendingMessageIndex], message)
      );
    } else {
      // Aceleres fix (2026-06-26): race condition entre fetch HTTP inicial e
      // broadcasts ActionCable de message.updated em msgs antigas (ex:
      // lib/aceleres/mark_outgoing_read_on_reply marca TODAS outgoing anteriores
      // como read quando o cliente responde -> cada update dispara message.updated
      // -> addMessage). Push cego no fim causava ordem [hoje, ontem, hoje].
      // Insere em ordem cronologica por created_at.
      const lastMessage = chat.messages[chat.messages.length - 1];
      const isNewer =
        !lastMessage ||
        new Date(message.created_at) >= new Date(lastMessage.created_at);
      if (isNewer) {
        chat.messages.push(message);
      } else {
        const insertIdx = chat.messages.findIndex(
          m => new Date(m.created_at) > new Date(message.created_at)
        );
        chat.messages.splice(insertIdx, 0, message);
      }
      // Side effects (timestamp/unread/scroll) so quando a msg e a mais nova
      if (isNewer) {
        chat.timestamp = message.created_at;
        const { conversation: { unread_count: unreadCount = 0 } = {} } =
          message;
        chat.unread_count = unreadCount;
        if (selectedChatId === conversationId) {
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        }
      }
    }

    // Keep the denormalized preview synchronized with the message collection.
    if (message.message_type !== MESSAGE_TYPE.ACTIVITY) {
      const currentPreview = chat.last_non_activity_message;
      const currentPreviewTime = currentPreview
        ? new Date(currentPreview.created_at).getTime()
        : 0;
      const messageTime = new Date(message.created_at).getTime();

      if (!currentPreview || messageTime >= currentPreviewTime) {
        chat.last_non_activity_message = message;
      }
    }
  },

  [types.UPDATE_MESSAGE_REACTIONS](
    { allConversations },
    { conversationId, messageId, reactions }
  ) {
    const chat = allConversations.find(c => c.id === conversationId);
    if (!chat) return;
    const idx = chat.messages.findIndex(m => m.id === messageId);
    if (idx === -1) return;
    const old = chat.messages[idx];
    // Substitui o item inteiro do array (mesmo padrão do ADD_MESSAGE) pra
    // forçar reatividade no allMessages computed do MessageList que faz
    // useCamelCase(deep:true) — só mutar a sub-prop pode passar batido
    // dependendo do camelcase-keys.
    chat.messages.splice(idx, 1, {
      ...old,
      content_attributes: { ...(old.content_attributes || {}), reactions },
    });
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    const exists = _state.allConversations.some(
      c => Number(c.id) === Number(conversation.id)
    );
    if (!exists) {
      _state.allConversations.push(rememberConversation(conversation));
    }
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    const conversation = getConversationById(_state)(conversationId);
    const cacheKey = conversationCacheKey(conversation);
    if (cacheKey) conversationSessionCache.delete(cacheKey);
    _state.allConversations = _state.allConversations.filter(
      c => Number(c.id) !== Number(conversationId)
    );
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    // WebSocket e REST podem serializar o mesmo id como string ou numero.
    // Comparar estritamente injeta uma segunda conversa sem is_pinned quando
    // chega uma nova mensagem, fazendo o item parecer desafixado.
    const index = allConversations.findIndex(
      c => Number(c.id) === Number(conversation.id)
    );

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // ignore out of order events
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }

      const { messages, ...updates } = conversation;
      const preservedUpdates =
        selectedConversation.is_pinned && updates.is_pinned === false
          ? {
              ...updates,
              is_pinned: true,
              pinned_at: selectedConversation.pinned_at,
            }
          : updates;
      allConversations[index] = rememberConversation({
        ...selectedConversation,
        ...preservedUpdates,
        ...(pendingStatusFor(conversation) || {}),
        ...(pendingStartFor(conversation) || {}),
      });
      if (Number(_state.selectedChatId) === Number(conversation.id)) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      const { conversationType } = _state.conversationFilters || {};
      const { aceleresBox, assigneeType } = _state.conversationFilters || {};
      const { MENTION, PARTICIPATING } = wootConstants.CONVERSATION_TYPE;
      const showPersonalChats =
        aceleresBox === 'pessoais' || assigneeType === 'personal';
      // As caixas Aceleres têm regras de posse, equipe, status e conexão que
      // não viajam no broadcast da conta. Uma conversa ausente só pode entrar
      // nelas pela listagem autoritativa; aceitar UPDATE_CONVERSATION aqui
      // fazia cartões de outras equipes aparecerem até o próximo refresh.
      if (aceleresBox) return;
      // Não injetar conversas pessoais via WebSocket —
      // elas só entram no store via carga inicial do servidor (aba Pessoais).
      const attributes = conversation.additional_attributes || {};
      const isPersonal =
        Number(attributes.personal_owner_chatwoot_user_id) > 0 ||
        Number(attributes.personal_origin_chatwoot_user_id) > 0 ||
        conversation.aceleres_personal_box === true;
      if (
        ![MENTION, PARTICIPATING].includes(conversationType) &&
        (showPersonalChats || !isPersonal)
      ) {
        _state.allConversations.push(rememberConversation(conversation));
      }
    }
  },

  [types.UPDATE_CONVERSATION_PIN](
    _state,
    { conversationId, isPinned, pinnedAt }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.is_pinned = Boolean(isPinned);
      chat.pinned_at = pinnedAt || null;
      rememberConversation(chat);
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.SET_LIST_ERROR_STATUS](_state) {
    _state.listErrorStatus = true;
  },

  [types.CLEAR_LIST_ERROR_STATUS](_state) {
    _state.listErrorStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getConversationById(_state)(payload.id);
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_CONVERSATION_CALL_STATUS](
    _state,
    { conversationId, callStatus }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    chat.additional_attributes = {
      ...chat.additional_attributes,
      call_status: callStatus,
    };
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](_state, { conversationId, callStatus }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const lastCall = (chat.messages || []).findLast(
      m => m.content_type === CONTENT_TYPES.VOICE_CALL
    );

    if (!lastCall) return;

    lastCall.content_attributes ??= {};
    lastCall.content_attributes.data = {
      ...lastCall.content_attributes.data,
      status: callStatus,
    };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
